import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, Building2, GitBranch, MessageCircle, ShoppingCart } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  LabelList
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { fetchCalls, fetchElectrobikeFunnel } from '../api';

const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#0ea5e9', '#7c3aed', '#64748b'];
const tooltipWrapperStyle = { zIndex: 30 };

const formatPercent = (value, total) => {
  if (!total) return '0%';
  return `${((value * 100) / total).toFixed(1)}%`;
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;
  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md"
      style={{
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{item.label}</div>
      <div>Звонков: {item.count.toLocaleString('ru-RU')}</div>
      <div>Доля: {formatPercent(item.count, item.total)}</div>
    </div>
  );
};

const renderBarLabel = ({ x, y, width, height, value }) => {
  if (value === null || value === undefined) return null;
  return (
    <text
      x={(x ?? 0) + (width ?? 0) + 6}
      y={(y ?? 0) + (height ?? 0) / 2}
      dominantBaseline="middle"
      fill="hsl(var(--foreground))"
      fontSize="11"
      fontWeight="600"
    >
      {value}
    </text>
  );
};

const summarize = (items, keyGetter, labelGetter, limit = 8, order = null) => {
  const counts = new Map();
  const labels = new Map();
  items.forEach((item) => {
    const key = keyGetter(item);
    if (!key) return;
    counts.set(key, (counts.get(key) ?? 0) + 1);
    labels.set(key, labelGetter(item, key));
  });

  const total = items.length;
  const rows = Array.from(counts.entries()).map(([key, count]) => ({
    key,
    label: labels.get(key) ?? key,
    count,
    total,
    valueLabel: `${count} (${formatPercent(count, total)})`
  }));

  const ordered = order
    ? rows.sort((left, right) => {
      const leftIndex = order.indexOf(left.key);
      const rightIndex = order.indexOf(right.key);
      return (leftIndex === -1 ? 999 : leftIndex) - (rightIndex === -1 ? 999 : rightIndex)
        || right.count - left.count;
    })
    : rows.sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, 'ru'));

  return ordered.slice(0, limit);
};

const MiniBarChart = ({ title, description, icon: Icon, data, onSelect }) => {
  const height = Math.max(220, data.length * 38 + 44);
  return (
    <Card>
      <CardHeader className="space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground">Нет данных для отображения.</p>
        ) : (
          <div className="w-full min-w-0 rounded-md bg-muted/30 px-2 py-4" style={{ height }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
              <BarChart data={data} layout="vertical" margin={{ top: 8, right: 96, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="label" width={190} tick={{ fontSize: 11 }} interval={0} />
                <RechartsTooltip wrapperStyle={tooltipWrapperStyle} content={<ChartTooltip />} />
                <Bar
                  dataKey="count"
                  name="Звонки"
                  radius={[0, 4, 4, 0]}
                  maxBarSize={26}
                  cursor={onSelect ? 'pointer' : 'default'}
                  onClick={(entry) => onSelect?.(entry?.payload ?? entry)}
                >
                  {data.map((item, index) => (
                    <Cell key={item.key} fill={COLORS[index % COLORS.length]} />
                  ))}
                  <LabelList dataKey="valueLabel" content={renderBarLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const FUNNEL_MODES = [
  { code: 'sales', label: 'Продажи', icon: ShoppingCart },
  { code: 'consultation', label: 'Консультации', icon: MessageCircle }
];

const FUNNEL_STAGE_FILTERS = {
  sales_calls: { service_area: 'website_order' },
  applicable: { service_area: 'website_order' },
  attempt: { funnel_stage: 'attempt' },
  alternative_offered: { objection: 'no_availability', criterion: 'offered_alternative', criterion_max_score: 1 },
  result: { funnel_stage: 'success' },
  consultation_calls: { primary_intent: 'information_request' },
  answered_question: { criterion: 'answered_question' },
  identified_need: { criterion: 'identified_need' },
  fixed_next_step: { criterion: 'fixed_next_step' }
};

const FunnelStageList = ({ stages, onOpenCallJournal }) => {
  const maxCalls = Math.max(1, ...(stages ?? []).map((stage) => Number(stage.calls) || 0));

  if (!stages?.length) {
    return <p className="text-sm text-muted-foreground">Нет данных для отображения.</p>;
  }

  return (
    <div className="space-y-3">
      {stages.map((stage) => {
        const calls = Number(stage.calls) || 0;
        const share = Number(stage.shareOfFirstStage) || 0;
        return (
          <button
            key={stage.key}
            type="button"
            onClick={() => onOpenCallJournal?.(FUNNEL_STAGE_FILTERS[stage.key])}
            className="w-full rounded-md border bg-muted/20 p-3 text-left transition-colors hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">{stage.title}</div>
                <div className="mt-1 text-xs text-muted-foreground">{stage.description}</div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-sm font-semibold">{calls.toLocaleString('ru-RU')}</div>
                <div className="text-xs text-muted-foreground">{share.toFixed(1)}%</div>
              </div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-gray-200 dark:bg-gray-800">
              <div
                className="h-2 rounded-full bg-blue-600"
                style={{ width: `${Math.max(4, Math.min(100, (calls * 100) / maxCalls))}%` }}
              />
            </div>
          </button>
        );
      })}
    </div>
  );
};

const AlternativeBadge = ({ funnel }) => {
  const applicable = Number(funnel?.alternativeApplicableCalls) || 0;
  const offered = Number(funnel?.alternativeOfferedCalls) || 0;
  const rate = Number(funnel?.alternativeOfferRate) || 0;
  const isWeak = applicable > 0 && rate < 50;

  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          Альтернативный товар при отсутствии позиции
        </div>
        <Badge
          variant="outline"
          className={isWeak ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}
        >
          {applicable === 0 ? 'нет случаев' : `${rate.toFixed(1)}%`}
        </Badge>
      </div>
      <div className="mt-2 text-xs text-muted-foreground">
        Предложили другой товар в {offered.toLocaleString('ru-RU')} из {applicable.toLocaleString('ru-RU')} случаев, где нужной позиции не было.
      </div>
    </div>
  );
};

const ElectrobikeFunnelCard = ({ funnel, mode, onModeChange, onOpenCallJournal }) => {
  const selectedMode = FUNNEL_MODES.find((item) => item.code === mode) ?? FUNNEL_MODES[0];
  const Icon = selectedMode.icon;

  return (
    <Card>
      <CardHeader className="space-y-0 pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5" />
              Воронка Электробайка
            </CardTitle>
            <CardDescription>
              Продажи и консультации без общей абстрактной воронки
            </CardDescription>
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon className="h-4 w-4" />
            <select
              value={mode}
              onChange={(event) => onModeChange(event.target.value)}
              className="rounded-md border bg-background px-2 py-1 text-sm text-foreground"
            >
              {FUNNEL_MODES.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {mode === 'sales' && <AlternativeBadge funnel={funnel} />}
        <FunnelStageList stages={funnel?.stages ?? []} onOpenCallJournal={onOpenCallJournal} />
      </CardContent>
    </Card>
  );
};

const normalizeDateRange = (dateRange) => {
  if (!dateRange?.from || !dateRange?.to) {
    return null;
  }
  return {
    date_from: format(dateRange.from, 'yyyy-MM-dd'),
    date_to: format(dateRange.to, 'yyyy-MM-dd')
  };
};

const ElectrobikeDashboardWidgets = ({ dateRange, callType = 'all', onOpenCallJournal }) => {
  const [items, setItems] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [funnelMode, setFunnelMode] = useState('sales');
  const [error, setError] = useState(null);
  const isFetching = useRef(false);

  const params = useMemo(() => {
    const range = normalizeDateRange(dateRange);
    if (!range) return null;
    return {
      ...range,
      call_type: callType === 'all' ? undefined : callType,
      limit: 500
    };
  }, [callType, dateRange]);

  const funnelParams = useMemo(() => (
    params ? { ...params, mode: funnelMode } : null
  ), [funnelMode, params]);

  const load = useCallback(async () => {
    if (!params || !funnelParams || isFetching.current) return;
    setError(null);
    isFetching.current = true;
    try {
      const [response, funnelResponse] = await Promise.all([
        fetchCalls(params),
        fetchElectrobikeFunnel(funnelParams)
      ]);
      setItems(Array.isArray(response?.items) ? response.items : []);
      setFunnel(funnelResponse ?? null);
    } catch {
      setError('Не удалось загрузить срезы Электробайка.');
      setItems([]);
      setFunnel(null);
    } finally {
      isFetching.current = false;
    }
  }, [funnelParams, params]);

  useEffect(() => {
    load();
  }, [load]);

  const locations = useMemo(
    () => summarize(items, (item) => item.employeeName, (item) => item.employeeName ?? 'Без салона', 8),
    [items]
  );
  return (
    <div className="grid gap-6">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <ElectrobikeFunnelCard
        funnel={funnel}
        mode={funnelMode}
        onModeChange={setFunnelMode}
        onOpenCallJournal={onOpenCallJournal}
      />

      <MiniBarChart
        title="Салоны и общая линия"
        description="Куда звонят клиенты"
        icon={Building2}
        data={locations}
        onSelect={(item) => onOpenCallJournal?.({ department: item.key })}
      />
    </div>
  );
};

export default ElectrobikeDashboardWidgets;
